import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ListButton, SaveButton } from "@refinedev/antd";
import {
  Button,
  Checkbox,
  Collapse,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Result,
  Select,
  Spin,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  LeftOutlined,
  ScissorOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/utilities";
import { confirmFooter } from "@/components/modals/confirmFooter";
import {
  OptimizerField,
  OptimizerFieldValue,
  getSellerOptimizerConfig,
  maskToValues,
  updateSellerOptimizerConfig,
  valuesToMask,
} from "./api";
import { PlannerConnection } from "./PlannerConnection";
import {
  getPlannerConnection,
  isPlannerConfigured,
} from "@/services/planner.api";

/**
 * El orden y el nombre de cada sección. Las tres primeras son las del admin de Magento, con sus
 * nombres; "Avanzado" va cerrada. Un grupo nuevo del backend cae al final con su key.
 */
const GROUPS: { key: string; label: string }[] = [
  { key: "general", label: "Configuración general" },
  { key: "optimization", label: "Configuración de optimización" },
  { key: "material", label: "Configuración de Material" },
  { key: "new", label: "Configuraciones nuevas" },
  { key: "report", label: "Plano y archivo de la máquina" },
  { key: "labels", label: "Rótulos" },
  { key: "advanced", label: "Avanzado (Lepton)" },
];

/**
 * La grilla de los campos, como la del Catálogo (xcons-docs#552): uno al lado del otro en el orden
 * del formulario, tantos como entren. Con 280px de mínimo entran tres en una notebook de 1280px
 * y más en una pantalla grande; en una chica bajan solos a una columna.
 */
const GRILLA: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  columnGap: 24,
  rowGap: 16,
  alignItems: "start",
};

/** El espacio entre filas lo pone la grilla, no el margen de cada `Form.Item`. */
const CELDA: React.CSSProperties = { marginBottom: 0 };

/**
 * El nombre de una lista de casillas no es el de un input: titula un bloque de opciones. Va como
 * subtítulo, en negrita y 2px menos que el encabezado de la sección (xcons-docs#552).
 */
const SUBTITULO: React.CSSProperties = { fontWeight: 600, fontSize: 12 };

/** Las casillas ocupan la fila entera: en una celda, sus 3 columnas parten cada opción. */
const celda = (field: OptimizerField): React.CSSProperties =>
  field.type === "mask" ? { ...CELDA, gridColumn: "1 / -1" } : CELDA;

/** Las secciones que arrancan abiertas: todas menos lo avanzado. */
const CLOSED_GROUPS = new Set(["advanced"]);

/** Como el `Yesno` de Magento: un select, no un switch. */
const YES_NO = [
  { value: true, label: "Sí" },
  { value: false, label: "No" },
];

/** Del valor de la API al del control: una máscara se edita como la lista de sus opciones. */
const toFormValue = (
  field: OptimizerField,
  value: OptimizerFieldValue | null,
) =>
  field.type === "mask" ? maskToValues(field, value as number | null) : value;

const fromFormValue = (
  field: OptimizerField,
  value: unknown,
): OptimizerFieldValue | null => {
  if (field.type === "mask") {
    const bits = (value as number[] | undefined) ?? [];
    return bits.length ? valuesToMask(bits) : null;
  }
  if (value === undefined || value === "") return null;
  return value as OptimizerFieldValue | null;
};

const formatDefault = (field: OptimizerField): string => {
  if (field.default === null) return "sin valor";
  if (field.type === "boolean") return field.default ? "Sí" : "No";
  if (field.type === "enum") {
    return String(
      field.options?.find((o) => o.value === field.default)?.label ??
        field.default,
    );
  }
  if (field.type === "mask") {
    const labels = maskToValues(field, field.default as number).map(
      (bit) =>
        field.options?.find((o) => Number(o.value) === bit)?.label ?? bit,
    );
    return labels.length ? labels.join(", ") : "ninguna";
  }
  return String(field.default);
};

const renderControl = (field: OptimizerField) => {
  switch (field.type) {
    case "boolean":
      return (
        <Select
          options={YES_NO}
          allowClear={field.default === null}
          placeholder={field.default === null ? "Sin valor" : undefined}
        />
      );
    case "number":
    case "integer":
      return (
        <InputNumber
          min={field.min}
          max={field.max}
          precision={field.type === "integer" ? 0 : undefined}
          style={{ width: "100%" }}
          placeholder={
            field.default === null ? "Sin valor" : String(field.default)
          }
        />
      );
    case "string":
      return <Input maxLength={field.maxLength} allowClear />;
    case "enum":
      return (
        <Select
          options={field.options}
          allowClear={field.default === null}
          placeholder={field.default === null ? "Sin valor" : undefined}
        />
      );
    case "mask":
      return (
        <Checkbox.Group
          className="casillas-arriba"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "4px 16px",
            alignItems: "start",
          }}
        >
          {(field.options ?? []).map((o) => (
            <Checkbox key={String(o.value)} value={Number(o.value)}>
              {o.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      );
  }
};

export const SellerOptimizerConfigList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const sellerCode = decodeURIComponent(id ?? "");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** Sin datos, el form no se edita: guardar un form vacío pisaría la config con defaults. */
  const [loadError, setLoadError] = useState<"forbidden" | "error" | null>(
    null,
  );
  const [fields, setFields] = useState<OptimizerField[]>([]);
  const [hasLeptonAccount, setHasLeptonAccount] = useState(false);
  /** El usuario de Lepton guardado: el input arranca con él y sólo viaja si se cambió. */
  const [leptonUserId, setLeptonUserId] = useState<string | null>(null);
  /** Los campos que el usuario devolvió a su default en esta edición: viajan como `null`. */
  const [resetKeys, setResetKeys] = useState<Set<string>>(new Set());
  /**
   * Los campos que el usuario cambió en esta edición. Guardar manda sólo esos: un campo que nadie
   * tocó no viaja, aunque la conversión del control no devuelva exactamente el valor guardado.
   */
  const [touchedKeys, setTouchedKeys] = useState<Set<string>>(new Set());
  /** Planner es sólo para los sellers que habilita el servicio: ante la duda, no se muestra. */
  const [plannerEnabled, setPlannerEnabled] = useState(false);

  const load = (data: Awaited<ReturnType<typeof getSellerOptimizerConfig>>) => {
    setFields(data.fields);
    setHasLeptonAccount(data.hasLeptonAccount);
    setLeptonUserId(data.leptonUserId);
    setResetKeys(new Set());
    setTouchedKeys(new Set());
    form.resetFields();
    form.setFieldsValue({
      leptonUserId: data.leptonUserId ?? "",
      ...Object.fromEntries(
        data.fields.map((f) => [f.key, toFormValue(f, f.value)]),
      ),
    });
  };

  useEffect(() => {
    if (!sellerCode) return;
    setLoading(true);
    getSellerOptimizerConfig(sellerCode)
      .then(load)
      .catch((e: any) => {
        const forbidden = e?.response?.status === 403;
        setLoadError(forbidden ? "forbidden" : "error");
        message.error(
          forbidden
            ? "No tenés permisos para ver la configuración del optimizador de este seller"
            : "No se pudo cargar la configuración del optimizador",
        );
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  useEffect(() => {
    setPlannerEnabled(false);
    if (!sellerCode || !isPlannerConfigured()) return;
    // La respuesta de un seller anterior no pisa la del actual.
    let current = true;
    getPlannerConnection(sellerCode)
      .then((c) => current && setPlannerEnabled(c.enabled === true))
      .catch(() => current && setPlannerEnabled(false));
    return () => {
      current = false;
    };
  }, [sellerCode]);

  const resetToDefault = (field: OptimizerField) => {
    form.setFieldValue(field.key, toFormValue(field, field.default));
    setResetKeys((prev) => new Set(prev).add(field.key));
  };

  // Sólo lo dispara una edición del usuario: `setFieldsValue` no pasa por acá.
  const handleValuesChange = (changed: Record<string, unknown>) => {
    const touched = Object.keys(changed);
    setTouchedKeys((prev) => new Set([...prev, ...touched]));
    if (!touched.some((k) => resetKeys.has(k))) return;
    setResetKeys((prev) => {
      const next = new Set(prev);
      touched.forEach((k) => next.delete(k));
      return next;
    });
  };

  const handleSave = async () => {
    const raw = form.getFieldsValue(true);
    const values: Record<string, OptimizerFieldValue | null> = {};
    for (const field of fields) {
      if (resetKeys.has(field.key)) {
        values[field.key] = null;
        continue;
      }
      if (!touchedKeys.has(field.key)) continue;
      const next = fromFormValue(field, raw[field.key]);
      if (next !== field.value) values[field.key] = next;
    }
    const typedUserId: string = touchedKeys.has("leptonUserId")
      ? String(raw.leptonUserId ?? "").trim()
      : leptonUserId ?? "";
    // Vaciar el input no saca la cuenta: eso es "Quitar la cuenta de Lepton", que pide confirmación.
    if (leptonUserId && !typedUserId) {
      message.error('Para sacar la cuenta usá "Quitar la cuenta de Lepton".');
      return;
    }
    const newUserId: string | undefined =
      typedUserId && typedUserId !== (leptonUserId ?? "")
        ? typedUserId
        : undefined;

    if (!Object.keys(values).length && newUserId === undefined) {
      message.info("No hay cambios para guardar");
      return;
    }

    setSaving(true);
    try {
      const saved = await updateSellerOptimizerConfig(sellerCode, {
        ...(newUserId !== undefined ? { leptonUserId: newUserId } : {}),
        values,
      });
      load(saved);
      message.success("Configuración del optimizador guardada");
    } catch (e: any) {
      // El backend valida contra el catálogo y devuelve el error de cada campo.
      const errors: Record<string, string> | undefined =
        e?.response?.data?.errors;
      if (errors) {
        form.setFields(
          Object.entries(errors).map(([name, error]) => ({
            name,
            errors: [error],
          })),
        );
        message.error("Hay campos inválidos: no se guardó nada");
      } else {
        message.error(getErrorMessage(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const removeLeptonAccount = async () => {
    setSaving(true);
    try {
      load(await updateSellerOptimizerConfig(sellerCode, { leptonUserId: "" }));
      message.success(
        "Se quitó la cuenta de Lepton: el seller deja de ofrecer el optimizador",
      );
    } catch (e: any) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /** Como las confirmaciones del Catálogo: "Atrás" a la izquierda y "Sí, …" primario a la derecha. */
  const confirmRemoveLeptonAccount = () =>
    Modal.confirm({
      title: "¿Quitar la cuenta de Lepton?",
      content:
        "Los tableros de este seller dejan de ofrecer el optimizador. La configuración se conserva.",
      okText: "Sí, quitar",
      cancelText: "Atrás",
      cancelButtonProps: { icon: <LeftOutlined /> },
      footer: confirmFooter,
      onOk: removeLeptonAccount,
    });

  const groups = useMemo(() => {
    const known = GROUPS.filter((g) => fields.some((f) => f.group === g.key));
    const unknown = [...new Set(fields.map((f) => f.group))]
      .filter((g) => !GROUPS.some((k) => k.key === g))
      .map((key) => ({ key, label: key }));
    return [...known, ...unknown];
  }, [fields]);

  const formProps = {
    form,
    /** El nombre arriba del control y la descripción abajo (xcons-docs#552). */
    layout: "vertical" as const,
    onValuesChange: handleValuesChange,
  };

  /**
   * El usuario de Lepton encabeza "Configuración general", como en Magento, en una fila propia: es
   * la cuenta, no un parámetro más. El input mide lo que una columna y la indicación va en una línea.
   * "Quitar la cuenta" va al lado y pide confirmación: apaga el optimizador del seller.
   */
  const accountItems = (
    <Form.Item
      label="Usuario de API Lepton"
      style={{ ...CELDA, gridColumn: "1 / -1" }}
      extra={
        <>
          El usuario que da Lepton Sistemas al contratar su API.{" "}
          {hasLeptonAccount
            ? "Para cambiarlo, reemplazalo y guardá."
            : "Sin cuenta, los tableros de este seller no ofrecen el optimizador."}
        </>
      }
    >
      <Flex gap={8} align="center">
        {/*
         * Un input común, a la vista: enmascarado, Chrome lo autocompletaba con una contraseña
         * guardada y el guardado la mandaba como usuario de Lepton. Los `data-*` lo apagan en los
         * gestores de contraseñas que no respetan `autoComplete`.
         */}
        <Form.Item name="leptonUserId" noStyle>
          <Input
            autoComplete="off"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            style={{ maxWidth: 420 }}
            placeholder="User ID de Lepton"
          />
        </Form.Item>
        {hasLeptonAccount && (
          <Button disabled={saving} onClick={confirmRemoveLeptonAccount}>
            Quitar la cuenta de Lepton
          </Button>
        )}
      </Flex>
    </Form.Item>
  );

  const collapseItems = [
    ...groups.map((group) => ({
      key: group.key,
      label: <span style={{ fontWeight: 600 }}>{group.label}</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form {...formProps}>
          <div style={GRILLA}>
            {group.key === "general" && accountItems}
            {fields
              .filter((f) => f.group === group.key)
              .map((field) => {
                const isDefault =
                  resetKeys.has(field.key) ||
                  (field.isDefault && !form.isFieldTouched(field.key));
                return (
                  <Form.Item
                    key={field.key}
                    label={
                      field.type === "mask" ? (
                        <span style={SUBTITULO}>{field.label}</span>
                      ) : (
                        field.label
                      )
                    }
                    name={field.key}
                    style={celda(field)}
                    extra={
                      <Flex vertical gap={2} align="flex-start">
                        {field.help && <span>{field.help}</span>}
                        {/* Sólo cuando hay algo que restaurar: en el valor de fábrica, la descripción sola (xcons-docs#552). */}
                        {!isDefault && (
                          <Button
                            type="link"
                            size="small"
                            icon={<UndoOutlined />}
                            style={{ paddingInline: 0, fontSize: "inherit" }}
                            onClick={() => resetToDefault(field)}
                          >
                            Volver al valor original ({formatDefault(field)})
                          </Button>
                        )}
                      </Flex>
                    }
                  >
                    {renderControl(field)}
                  </Form.Item>
                );
              })}
          </div>
        </Form>
      ),
    })),
    // La conexión con Planner es de otro servicio: sin su URL en este ambiente, o si el servicio
    // no habilitó a este seller, no aparece.
    ...(plannerEnabled
      ? [
          {
            key: "planner",
            label: <span style={{ fontWeight: 600 }}>Lepton Planner</span>,
            styles: { header: { height: 40, alignItems: "center" } },
            children: <PlannerConnection sellerCode={sellerCode} />,
          },
        ]
      : []),
  ];

  // Hasta tener la config, sólo el spinner: con "Volver" y "Guardar" solos parecía que algo había
  // fallado (xcons-docs#552). Mismo spinner que el Catálogo.
  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Flex vertical style={{ height: "100%", minHeight: 0 }}>
      {/* Es una página del menú, no un detalle: va el ícono del menú y no la flecha de volver (xcons-docs#509). */}
      <Flex align="center" gap={8} style={{ marginBottom: 24, flexShrink: 0 }}>
        <ScissorOutlined style={{ fontSize: 22 }} />
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t("optimizer.seller.title", "Optimizador de cortes")}
        </Typography.Title>
      </Flex>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {loadError ? (
          <Result
            status={loadError === "forbidden" ? "403" : "error"}
            title={
              loadError === "forbidden"
                ? "No tenés permisos para ver esta configuración"
                : "No se pudo cargar la configuración"
            }
            subTitle={
              loadError === "forbidden"
                ? "Pedile a un administrador del seller el permiso de Optimizer."
                : "Volvé a intentar en un momento."
            }
          />
        ) : (
          <Collapse
            defaultActiveKey={[
              ...groups
                .filter((g) => !CLOSED_GROUPS.has(g.key))
                .map((g) => g.key),
              "planner",
            ]}
            items={collapseItems}
          />
        )}
      </div>

      <div style={{ paddingTop: 32, paddingBottom: 16, flexShrink: 0 }}>
        <Flex justify="space-between">
          <ListButton icon={<LeftOutlined />} onClick={() => navigate(-1)}>
            {t("buttons.back", "Volver")}
          </ListButton>
          {!loadError && (
            <SaveButton
              loading={saving}
              onClick={handleSave}
              icon={<CheckOutlined />}
              iconPosition="end"
            >
              {t("buttons.save", "Guardar")}
            </SaveButton>
          )}
        </Flex>
      </div>
    </Flex>
  );
};
